import axios, { AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { apiConfig } from '@config/api.config';

declare module 'axios' {
  export interface InternalAxiosRequestConfig {
    inpronexusAbortController?: AbortController;
  }
}

/**
 * Cliente HTTP único de la app.
 * - withCredentials: true → cookies Sanctum.
 * - Antes del primer POST/PUT/PATCH/DELETE, llama a /sanctum/csrf-cookie
 *   (solo una vez por sesión).
 * - 401 → redirect al login, salvo en la pantalla pública de firma NCp,
 *   que gestiona el acceso dentro del propio flujo móvil.
 */
export const api = axios.create({
  baseURL: apiConfig.baseURL,
  timeout: apiConfig.timeout,
  withCredentials: true,
  headers: {
    'X-Requested-With': 'XMLHttpRequest',
    Accept: 'application/json',
  },
});

let csrfReady = false;
const NEEDS_CSRF = new Set(['post', 'put', 'patch', 'delete']);

const activeRequestControllers = new Set<AbortController>();

function cleanupRequestController(config?: InternalAxiosRequestConfig | null): void {
  const controller = config?.inpronexusAbortController;

  if (controller) {
    activeRequestControllers.delete(controller);
    delete config.inpronexusAbortController;
  }
}

export function abortActiveRequests(reason = 'session-ended'): void {
  for (const controller of activeRequestControllers) {
    controller.abort(reason);
  }

  activeRequestControllers.clear();
}

export function isRequestCancellation(error: unknown): boolean {
  return axios.isCancel(error)
    || (
      typeof error === 'object'
      && error !== null
      && 'name' in error
      && (error as { name?: string }).name === 'CanceledError'
    )
    || (
      typeof error === 'object'
      && error !== null
      && 'code' in error
      && (error as { code?: string }).code === 'ERR_CANCELED'
    );
}


function readXsrfCookie(): string | null {
  const match = document.cookie.match(/(^|;\s*)XSRF-TOKEN=([^;]+)/);
  return match ? decodeURIComponent(match[2]) : null;
}

api.interceptors.request.use(async (config) => {
  if (!config.signal) {
    const controller = new AbortController();
    config.signal = controller.signal;
    config.inpronexusAbortController = controller;
    activeRequestControllers.add(controller);
  }

  const method = (config.method ?? 'get').toLowerCase();
  if (!csrfReady && NEEDS_CSRF.has(method)) {
    await axios.get(`${apiConfig.sanctumBaseURL}/sanctum/csrf-cookie`, {
      withCredentials: true,
    });
    csrfReady = true;
  }

  // Safety net: adjuntar manualmente X-XSRF-TOKEN.
  // Axios v1 a veces no lo hace para cross-origin requests aunque withCredentials sea true.
  if (NEEDS_CSRF.has(method)) {
    const xsrf = readXsrfCookie();
    if (xsrf) {
      config.headers['X-XSRF-TOKEN'] = xsrf;
    }
  }

  return config;
});

api.interceptors.response.use(
  (r) => {
    cleanupRequestController(r.config);
    return r;
  },
  (err: AxiosError) => {
    cleanupRequestController(err.config);
    const isLoginPage = window.location.pathname.startsWith('/login');
    const isNcpSignaturePage = window.location.pathname.startsWith('/firma/ncp/');

    if (err.response?.status === 401 && !isLoginPage && !isNcpSignaturePage) {
      window.location.href = '/login';
    }
    return Promise.reject(err);
  },
);

/** Util de tipos para extraer el data de un Resource Laravel */
export interface ResourceResponse<T> {
  data: T;
}
export interface CollectionResponse<T> {
  data: T[];
}
